import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // uses localStorage

import alertReducer from './pages/alerts/alertSlice.js';
import resourcesState from './pages/resources/resourceCardsSlice.js';
import releaseListReducer, {
  releaseListFilterMiddleware,
} from './pages/releases/release-list/releaseListSlice';
import releaseCalendarReducer, {
  releaseCalendarFilterMiddleware,
} from './pages/releases/calendar/releaseCalendarSlice';
import templatesReducer from './features/ag-grid/templates/templatesSlice.js';
import releaseDashboardReducer from './pages/releases/dashboard/releaseDashboardSlice.js';
import releaseSupportReducer from './pages/releases/support/releaseSupportSlice.js';
import releaseTestCasesDashboardReducer from './pages/release-test-cases/releaseTestCasesDashboardSlice.js';
import releasePivotReportReducer from './pages/releases/release-pivot-report/releasePivotReportSlice.js';
import releaseQADashboardReducer from './pages/reports/release-qa-dashboard/releaseQADashboardSlice.js';
import regressionDashboardReducer from './pages/reports/regression-dashboard/regressionDashboardSlice.js';
import knowledgeBaseReducer from './pages/agents/knowledge-base/knowledgeBaseSlice.js';
import filtersSliceReducer, { filterMiddleware } from './layout/common-filter';
import userReducer from './slices/userSlice.js';

// Combine all reducers
const rootReducer = combineReducers({
  user: userReducer,
  filters: filtersSliceReducer,
  alert: alertReducer,
  resources: resourcesState,
  releaseList: releaseListReducer,
  releaseCalendar: releaseCalendarReducer,
  releaseDashboard: releaseDashboardReducer,
  releaseSupport: releaseSupportReducer,
  templates: templatesReducer,
  releaseTestCasesDashboard: releaseTestCasesDashboardReducer,
  releasePivotReport: releasePivotReportReducer,
  releaseQADashboard: releaseQADashboardReducer,
  regressionDashboard: regressionDashboardReducer,
  knowledgeBase: knowledgeBaseReducer,
});

// Transform to exclude loading and uploading states from knowledgeBase persistence
const knowledgeBaseTransform = createTransform(
  // Transform state being persisted
  (inboundState, key) => {
    if (key === 'knowledgeBase') {
      const rest = { ...inboundState };
      delete rest.loading;
      delete rest.uploading;
      return rest;
    }
    return inboundState;
  },
  // Transform state being rehydrated
  (outboundState, key) => {
    if (key === 'knowledgeBase') {
      return {
        ...outboundState,
        loading: false,
        uploading: false,
      };
    }
    return outboundState;
  },
  { whitelist: ['knowledgeBase'] },
);

// Whitelist Persist only this slice
// Note: 'filters' removed from whitelist to reset filters on page refresh
const persistConfig = {
  key: 'root',
  storage,
  whitelist: [
    'templates',
    'releaseDashboard',
    'releaseTestCasesDashboard',
    'releaseSupport',
    'releasePivotReport',
    'knowledgeBase',
  ],
  transforms: [knowledgeBaseTransform],
  // Whitelisting only controls writes; discard this slice from older saved sessions too.
  migrate: async (state) => {
    if (!state) return state;
    const nextState = { ...state };
    delete nextState.releaseQADashboard;
    return nextState;
  },
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      filterMiddleware,
      releaseListFilterMiddleware,
      releaseCalendarFilterMiddleware,
    ),
});

export const persistor = persistStore(store);
export default store;
